import loggerService from '@/services/logger.service';

export type EventTabStatus = 'primary' | 'secondary' | 'blocked';

type Unlock = () => void;

const FIRST_SLOT = 1;
const SECOND_SLOT = 2;

function slotLockName(eventId: string, slot: number) {
  return `gc-event-tab:${eventId}:${slot}`;
}

function requestLock(name: string, options: LockOptions): Promise<Unlock | null> {
  return new Promise((resolve, reject) => {
    navigator.locks
      .request(name, options, lock => {
        if (!lock) {
          resolve(null);
          return;
        }
        return new Promise<void>(unlock => resolve(unlock));
      })
      .catch(reject);
  });
}

export class EventTabClaim {
  private readonly eventId: string;
  private readonly onStatus: (status: EventTabStatus) => void;
  private readonly stopWaitingForFirstSlot = new AbortController();
  private unlockFirstSlot: Unlock | null = null;
  private unlockSecondSlot: Unlock | null = null;
  private released = false;

  constructor(eventId: string, onStatus: (status: EventTabStatus) => void) {
    this.eventId = eventId;
    this.onStatus = onStatus;
  }

  async start() {
    try {
      await this.takeFirstFreeSlot();
    } catch (error) {
      this.letTabInAfterLockError(error);
    }
  }

  release() {
    this.released = true;
    this.stopWaitingForFirstSlot.abort();
    this.unlockFirstSlot?.();
    this.unlockSecondSlot?.();
  }

  private async takeFirstFreeSlot() {
    this.unlockFirstSlot = await this.takeSlot(FIRST_SLOT, { ifAvailable: true });
    if (this.released) {
      return;
    }
    if (this.unlockFirstSlot) {
      this.onStatus('primary');
      return;
    }

    this.unlockSecondSlot = await this.takeSlot(SECOND_SLOT, { ifAvailable: true });
    if (this.released) {
      return;
    }
    if (!this.unlockSecondSlot) {
      this.onStatus('blocked');
      return;
    }

    this.onStatus('secondary');
    await this.moveUpWhenFirstSlotFrees();
  }

  private async moveUpWhenFirstSlotFrees() {
    this.unlockFirstSlot = await this.takeSlot(FIRST_SLOT, { signal: this.stopWaitingForFirstSlot.signal });
    if (this.released) {
      return;
    }
    this.unlockSecondSlot?.();
    this.unlockSecondSlot = null;
    this.onStatus('primary');
  }

  private async takeSlot(slot: number, options: LockOptions): Promise<Unlock | null> {
    const unlock = await requestLock(slotLockName(this.eventId, slot), options);
    if (unlock && this.released) {
      unlock();
      return null;
    }
    return unlock;
  }

  private letTabInAfterLockError(error: unknown) {
    if (this.released) {
      return;
    }
    loggerService.warn('[eventTabLimit] Web Lock request failed, letting the tab in', { error });
    this.onStatus('primary');
  }
}

export function claimEventTab(eventId: string, onStatus: (status: EventTabStatus) => void): EventTabClaim {
  const claim = new EventTabClaim(eventId, onStatus);
  void claim.start();
  return claim;
}
